import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Sun, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Reveal } from '../ui-custom/Reveal';
import { theme } from '../../utils/theme';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const BenefitRow = ({ text, delay }) => {
  return (
    <Reveal delay={delay}>
      <li className="flex items-start gap-3 group">
        <div className="p-1 rounded-full bg-teal-900/30 text-teal-400 mt-0.5 group-hover:bg-teal-400 group-hover:text-black transition-colors">
          <CheckCircle2 size={16} />
        </div>
        <span className="text-lg text-gray-300 group-hover:text-white transition-colors">
          {text}
        </span>
      </li>
    </Reveal>
  );
};

export const ProductSection = ({ systemActive }) => {
  const { t, language } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/products`);
        if (response.data && response.data.length > 0) {
          setProduct(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        // Fallback features if API fails
        setProduct({
          features: [
            "Blocage jusqu'à 98% des UV (été)",
            "Réflexion jusqu'à 85% des IR (hiver)",
            "Installation sans perçage (clip-on)",
            "Totalement autonome",
            "Automatisation prédictive basée sur le rayonnement solaire et optimisé par l'IA",
            "Garantie 5 ans"
          ],
          features_en: [
            "Blocks up to 98% of UV (summer)",
            "Reflects up to 85% of IR (winter)",
            "Clip-on installation, no drilling",
            "Fully autonomous",
            "Predictive automation based on solar radiation and optimized by AI",
            "5-year warranty"
          ]
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, []);

  // --- CORRECTION DE SÉCURITÉ ---
  // On s'assure que 'features' est toujours un tableau, jamais null ou undefined
  const features = product 
    ? (language === 'en' && product.features_en ? product.features_en : product.features) || []
    : [];

  return (
    <section 
      id="lefiltre" 
      className="py-24 relative overflow-hidden border-t" 
      style={{ borderColor: theme.colors.border, backgroundColor: '#050505'}}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(231,165,95,0.05),_transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-4 lg:flex items-center gap-16 relative z-10">
        
        {/* Interactive Window Visual */}
        <Reveal className="lg:w-1/2">
          <div className="relative h-[500px] rounded-2xl overflow-hidden border-8 border-[#1a1a1a] shadow-2xl bg-gray-900 group">
            {/* Outside World */}
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80')] bg-cover bg-center transition-transform duration-[10s] ease-linear group-hover:scale-110" />
            
            {/* Sun Flare */}
            <div className="absolute top-10 right-10 w-32 h-32 bg-yellow-400 rounded-full blur-[80px] opacity-60 mix-blend-screen animate-pulse" />

            {/* The Smart Filter Overlay (Animated Slats Effect) */}
            <div className="absolute inset-0 flex flex-col pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <div 
                  key={i} 
                  className="flex-1 w-full bg-black/80 backdrop-blur-[2px] border-b border-white/5 transition-all duration-700 ease-in-out"
                  style={{ 
                    transform: systemActive ? 'rotateX(0deg) translateY(0)' : 'rotateX(90deg) translateY(-10px)',
                    opacity: systemActive ? 1 : 0,
                    transitionDelay: `${i * 30}ms`
                  }}
                />
              ))}
            </div>

            {/* Glass Reflection */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-40 pointer-events-none" />

            {/* HUD / Labels */}
            <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
              <div className="bg-black/60 backdrop-blur-md px-4 py-3 rounded-xl border border-white/10 text-xs shadow-lg transform transition-transform hover:scale-105">
                <div className="flex items-center gap-2 mb-2">
                  <Sun 
                    size={14} 
                    className={systemActive ? 'text-orange-500' : 'text-yellow-400'} 
                  />
                  <span className="text-gray-300 font-mono">UV INDEX: HIGH</span>
                </div>
                <div className="h-1 w-32 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-green-500 to-red-500 w-[80%]" />
                </div>
              </div>

              <div className={`px-4 py-2 rounded-lg font-bold text-xs backdrop-blur-md transition-colors duration-500 border ${
                systemActive 
                  ? 'bg-orange-500/20 border-orange-500 text-orange-400' 
                  : 'bg-white/10 border-white/20 text-white'
              }`}>
                {systemActive ? 'MODE: PROTECTION' : 'MODE: PASSIF'}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={200} className="lg:w-1/2 mt-12 lg:mt-0">
          <div className="inline-block px-3 py-1 mb-6 rounded-full text-xs font-bold border border-white/10 bg-white/5 text-white/70">
            {t.product.badge}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">
            {t.product.title1}<br />{t.product.title2}
          </h2>
          <p 
            className="text-lg leading-relaxed mb-8" 
            style={{ color: theme.colors.textMuted }}
          >
            {t.product.description}
          </p>
          
          <ul className="space-y-4">
            {/* --- CORRECTION DE SÉCURITÉ : AJOUT DE '?' --- */}
            {features?.map((feature, idx) => (
              <BenefitRow 
                key={idx}
                text={feature} 
                delay={300 + (idx * 50)} 
              />
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};